import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../store';
import { useNavigate } from 'react-router-dom';
import { useSocket } from '../hooks/useSocket';

const API = (() => {
  const h = window.location.hostname;
  return process.env.REACT_APP_API_URL || ((h === 'localhost' || h === '127.0.0.1') ? 'http://localhost:5001/api' : `http://${h}:5001/api`);
})();

interface DashboardStats {
  totalTransactions: number;
  totalRevenue: number;
  totalProducts: number;
  totalUsers: number;
  topProducts: Array<{ name: string; count: number }>;
  recentTransactions: Array<{ id: string; amount: number; date: string; isVoided: boolean }>;
  lowStockCount: number;
  activeBranches: number;
}

export const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toasts, setToasts] = useState<{ id: number; msg: string; color: string }[]>([]);

  const addToast = (msg: string, color: string) => {
    const id = Date.now();
    setToasts(prev => [...prev.slice(-2), { id, msg, color }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 5000);
  };

  // Realtime socket
  useSocket(user?.role, user?.branch_id, {
    onTransactionNew: (d) => {
      addToast(`💳 Transaksi baru Rp ${Number(d.total_amount).toLocaleString('id-ID')}`, '#27AE60');
      setStats(prev => prev ? { ...prev, totalTransactions: prev.totalTransactions + 1, totalRevenue: prev.totalRevenue + Number(d.total_amount) } : prev);
    },
    onBookingNew: (d) => addToast(`📋 Booking baru: ${d.customer_name}`, '#1A73E8'),
    onStockLow: (d) => addToast(`⚠️ Stok menipis: ${d.product_name} (${d.available} tersisa)`, '#E74C3C'),
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchStats();
  }, [user]);

  const fetchStats = async () => {
    setLoading(true);
    setError('');
    try {
      const headers = { Authorization: `Bearer ${token}` };

      const [summaryRes, productsRes, usersRes, topProductsRes, recentTxRes, branchesRes] =
        await Promise.all([
          axios.get(`${API}/reports/summary?period=month`, { headers }),
          axios.get(`${API}/products`, { headers }),
          axios.get(`${API}/users`, { headers }),
          axios.get(`${API}/reports/product-sales`, { headers }),
          axios.get(`${API}/transactions`, { headers }),
          axios.get(`${API}/branches`, { headers }),
        ]);

      const summary = summaryRes.data.data || summaryRes.data;
      const products = summaryRes.data.data ? productsRes.data.data : productsRes.data;
      const users = usersRes.data.data || usersRes.data;
      const topProducts = topProductsRes.data.data || topProductsRes.data;
      const recentTx = recentTxRes.data.data || recentTxRes.data;
      const branches = branchesRes.data.data || branchesRes.data;

      setStats({
        totalTransactions: summary.total_transactions ?? 0,
        totalRevenue: summary.total_revenue ?? 0,
        totalProducts: Array.isArray(products) ? products.length : 0,
        totalUsers: Array.isArray(users) ? users.length : 0,
        topProducts: (Array.isArray(topProducts) ? topProducts.slice(0, 5) : []).map(
          (p: any) => ({ name: p.name || 'Produk', count: parseInt(p.total_quantity) || 0 })
        ),
        recentTransactions: (Array.isArray(recentTx) ? recentTx.slice(0, 5) : []).map(
          (t: any) => ({
            id: t.transaction_number || t.id,
            amount: parseFloat(t.total_amount) || 0,
            date: t.created_at,
            isVoided: !!t.is_voided,
          })
        ),
        lowStockCount: 0,
        activeBranches: Array.isArray(branches)
          ? branches.filter((b: any) => b.is_active || b.status === 'active').length
          : 0,
      });
    } catch (err) {
      console.error('Failed to fetch dashboard stats:', err);
      setError('Gagal memuat data dashboard. Pastikan server backend berjalan.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.centered}>
        <div style={styles.spinner} />
        <p style={{ color: '#64748b', marginTop: 12 }}>Memuat dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.centered}>
        <p style={styles.errorText}>{error}</p>
        <button style={styles.retryBtn} onClick={fetchStats}>Coba Lagi</button>
      </div>
    );
  }

  const formatCurrency = (val: number) =>
    new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(val);

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <div style={styles.container}>
      {/* Realtime Toasts */}
      <div style={styles.toastContainer}>
        {toasts.map(t => (
          <div key={t.id} style={{ ...styles.toast, borderLeftColor: t.color }}>
            {t.msg}
          </div>
        ))}
      </div>

      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.headerTitle}>Dashboard</h1>
          <p style={styles.headerSub}>Selamat datang, {user?.name || 'Admin'} — data bulan ini</p>
        </div>
        <div style={styles.headerActions}>
          <button style={styles.refreshBtn} onClick={fetchStats}>↺ Refresh</button>
          <button onClick={logout} style={styles.logoutBtn}>Keluar</button>
        </div>
      </div>

      {/* Stats Cards */}
      <div style={styles.statsGrid}>
        <StatCard icon="💳" label="Transaksi Bulan Ini" value={stats!.totalTransactions.toLocaleString('id-ID')} color="#1A73E8" />
        <StatCard icon="💰" label="Omzet Bulan Ini" value={formatCurrency(stats!.totalRevenue)} color="#27ae60" />
        <StatCard icon="📦" label="Total Produk" value={stats!.totalProducts.toLocaleString('id-ID')} color="#f39c12" />
        <StatCard icon="👥" label="Total Pengguna" value={stats!.totalUsers.toLocaleString('id-ID')} color="#9b59b6" />
        <StatCard icon="🏢" label="Cabang Aktif" value={stats!.activeBranches.toLocaleString('id-ID')} color="#e67e22" />
      </div>

      {/* Charts Section */}
      <div style={styles.chartsContainer}>
        {/* Top Products */}
        <div style={styles.chartCard}>
          <h3 style={styles.cardTitle}>Produk Terlaris (Bulan Ini)</h3>
          {stats!.topProducts.length === 0 ? (
            <p style={styles.emptyText}>Belum ada data penjualan bulan ini</p>
          ) : (
            <div style={styles.list}>
              {stats!.topProducts.map((product, idx) => (
                <div key={idx} style={styles.listItem}>
                  <div style={styles.rankBadge}>{idx + 1}</div>
                  <span style={{ flex: 1, fontSize: 14 }}>{product.name}</span>
                  <span style={styles.countBadge}>{product.count} pcs</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Transactions */}
        <div style={styles.chartCard}>
          <h3 style={styles.cardTitle}>Transaksi Terbaru</h3>
          {stats!.recentTransactions.length === 0 ? (
            <p style={styles.emptyText}>Belum ada transaksi</p>
          ) : (
            <div style={styles.list}>
              {stats!.recentTransactions.map((trx) => (
                <div key={trx.id} style={{ ...styles.listItem, opacity: trx.isVoided ? 0.6 : 1 }}>
                  <div>
                    <p style={{ ...styles.txId, textDecoration: trx.isVoided ? 'line-through' : 'none' }}>{trx.id}</p>
                    <p style={styles.txDate}>{formatDate(trx.date)}</p>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {trx.isVoided && (
                      <span style={{ background: '#e74c3c', color: 'white', fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4 }}>VOID</span>
                    )}
                    <span style={{ ...styles.txAmount, color: trx.isVoided ? '#999' : '#27ae60' }}>{formatCurrency(trx.amount)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div style={styles.actionsContainer}>
        <h3 style={styles.cardTitle}>Akses Cepat</h3>
        <div style={styles.actionsGrid}>
          <ActionButton label="Pengguna" path="/users" icon="👥" />
          <ActionButton label="Add-Ons & Stok" path="/stock" icon="📦" />
          <ActionButton label="Transaksi" path="/transactions" icon="💳" />
          <ActionButton label="Laporan" path="/reports" icon="📊" />
          <ActionButton label="Cabang" path="/branches" icon="🏢" />
          <ActionButton label="Branch Config" path="/branch-config" icon="⚙️" />
        </div>
      </div>
    </div>
  );
};

const StatCard: React.FC<{ icon: string; label: string; value: string | number; color: string }> = ({
  icon, label, value, color,
}) => (
  <div style={{ ...styles.statCard, borderLeftColor: color }}>
    <div style={styles.statIcon}>{icon}</div>
    <div>
      <p style={styles.statLabel}>{label}</p>
      <h3 style={styles.statValue}>{value}</h3>
    </div>
  </div>
);

const ActionButton: React.FC<{ label: string; path: string; icon: string }> = ({ label, path, icon }) => {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => navigate(path)}
      style={styles.actionButton}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLButtonElement).style.background = '#f0f4ff';
        (e.currentTarget as HTMLButtonElement).style.borderColor = '#1A73E8';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLButtonElement).style.background = '#f9f9f9';
        (e.currentTarget as HTMLButtonElement).style.borderColor = '#e0e0e0';
      }}
    >
      <div style={styles.actionIcon}>{icon}</div>
      <span>{label}</span>
    </button>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: { padding: '24px', background: '#f5f7fa', minHeight: '100vh' },
  centered: {
    display: 'flex', flexDirection: 'column', justifyContent: 'center',
    alignItems: 'center', height: '100vh', gap: 12,
  },
  spinner: {
    width: 36, height: 36, border: '3px solid #e2e8f0',
    borderTop: '3px solid #1A73E8', borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  errorText: { color: '#dc2626', fontSize: 15 },
  retryBtn: {
    padding: '8px 20px', background: '#1A73E8', color: '#fff',
    border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14,
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: 24, background: 'white', padding: '20px 24px',
    borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0',
  },
  headerTitle: { margin: 0, fontSize: 22, fontWeight: 700, color: '#1e293b' },
  headerSub: { margin: '4px 0 0 0', fontSize: 13, color: '#64748b' },
  headerActions: { display: 'flex', gap: 10 },
  refreshBtn: {
    padding: '8px 16px', background: '#f1f5f9', color: '#475569',
    border: '1px solid #e2e8f0', borderRadius: 8, cursor: 'pointer', fontSize: 13,
  },
  logoutBtn: {
    padding: '8px 16px', background: '#fee2e2', color: '#dc2626',
    border: '1px solid #fca5a5', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600,
  },
  statsGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
    gap: 16, marginBottom: 24,
  },
  statCard: {
    background: 'white', padding: '18px 20px', borderRadius: 12,
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)', display: 'flex',
    alignItems: 'center', gap: 14, borderLeft: '4px solid', border: '1px solid #e2e8f0',
    borderLeftWidth: 4,
  },
  statIcon: { fontSize: 28 },
  statLabel: { margin: 0, color: '#64748b', fontSize: 12, fontWeight: 500 },
  statValue: { margin: '4px 0 0 0', fontSize: 20, fontWeight: 700, color: '#1e293b' },
  chartsContainer: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: 16, marginBottom: 24,
  },
  chartCard: {
    background: 'white', padding: '20px 24px', borderRadius: 12,
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0',
  },
  cardTitle: { margin: '0 0 16px 0', fontSize: 15, fontWeight: 700, color: '#1e293b' },
  emptyText: { color: '#94a3b8', fontSize: 13, fontStyle: 'italic' },
  list: { display: 'flex', flexDirection: 'column', gap: 8 },
  listItem: {
    display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
    background: '#f8fafc', borderRadius: 8, fontSize: 14,
  },
  rankBadge: {
    width: 22, height: 22, borderRadius: '50%', background: '#1A73E8',
    color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 11, fontWeight: 700, flexShrink: 0,
  },
  countBadge: {
    background: '#e0edff', color: '#1A73E8', padding: '2px 8px',
    borderRadius: 12, fontSize: 12, fontWeight: 600,
  },
  txId: { margin: 0, fontSize: 13, fontWeight: 600, color: '#1e293b' },
  txDate: { margin: '2px 0 0 0', fontSize: 11, color: '#94a3b8' },
  txAmount: { fontSize: 14, fontWeight: 700, color: '#27ae60', marginLeft: 'auto' },
  actionsContainer: {
    background: 'white', padding: '20px 24px', borderRadius: 12,
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0',
  },
  actionsGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
    gap: 12, marginTop: 12,
  },
  actionButton: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
    padding: '16px 8px', background: '#f9f9f9', border: '1px solid #e0e0e0',
    borderRadius: 10, cursor: 'pointer', transition: 'all 0.15s',
    fontSize: 13, fontWeight: 600, color: '#374151',
  },
  actionIcon: { fontSize: 26 },
  toastContainer: {
    position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
    display: 'flex', flexDirection: 'column', gap: 8,
  },
  toast: {
    background: '#fff', borderLeft: '4px solid', borderRadius: 8,
    padding: '12px 16px', fontSize: 13, fontWeight: 600, color: '#1e293b',
    boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
    animation: 'slideIn 0.2s ease-out',
    maxWidth: 320,
  },
};
